import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useSubmitTQProfileCompletionQuery,
} from '../../../../hooks/mutations/tqConnectMutations/user';
import { useTQConnectConfig } from '../../../../hooks/queries/tqConnectQueries';
import {
  useTQConnectUserProfileInfo,
  useTQProfileCompletionQueries,
} from '../../../../hooks/queries/tqConnectQueries/user';
import {
  ITQConnectConfig,
  ITQProfileCompletionQuery,
} from '../../../../types/tqConnectTypes';
import {
  ITQProfileCompletionQueryWithResponse,
  ITQProfileCompletionResponseRequest,
  ITQProfileCompletionState,
  ITQProfileInfo,
} from '../../../../types/tqConnectTypes/user';

interface ContextProps {
  userId: string;
  tqConnectConfig: ITQConnectConfig;
  tqProfileCompletionQueries: ITQProfileCompletionQuery[];
  tqProfileCompletionState: ITQProfileCompletionState;
  questionsMap: Record<string, ITQProfileCompletionQueryWithResponse>;
  questionsMapByIndex: { [key: number]: ITQProfileCompletionQueryWithResponse };
  currentIndex: number;
  setCurrentIndex: (i: number) => void;
  hasPrevQuestion: boolean;
  hasNextQuestion: boolean;
  isLoading: boolean;
  isSaving: boolean;
  showCompletionScreen: boolean;
  setShowCompletionScreen: (v: boolean) => void;
  closeSubmitTQProfileModal: () => void;
  updateTQProfileCompletionState: (
    newCompletionState: Partial<ITQProfileCompletionState>,
  ) => void;
  updateQuestionsMap: (
    questionId: string,
    question: Partial<ITQProfileCompletionQueryWithResponse>,
  ) => void;
  saveQuestionAnswer: (
    questionId: string,
    action: 'save' | 'submit' | 'clear',
    data: ITQProfileCompletionResponseRequest,
    saveToServer?: boolean,
  ) => Promise<void>;
  openCompletionScreen: () => void;
  submitTQProfileCompletionResponse: (
    blockToastAndClose?: boolean,
  ) => Promise<void>;
  goToPrevOrNextQuestion: (type: 'prev' | 'next') => void;
  goToNextUnattemptedQuestion: () => void;
  reset: () => void;
}

const SubmitTQProfileContext = createContext<ContextProps | null>(null);

export const useSubmitTQProfileContext = () => {
  const ctx = useContext(SubmitTQProfileContext);
  if (!ctx)
    throw new Error('SubmitTQProfileContext must be used within its provider');
  return ctx;
};

interface ProviderProps {
  children: React.ReactNode;
  userId: string;
  onClose: () => void;
  onSubmitSuccess?: () => void;
}

export const SubmitTQProfileProvider: React.FC<ProviderProps> = ({
  children,
  userId,
  onClose,
  onSubmitSuccess,
}) => {
  const { data: tqConnectConfigResult, isLoading: isTQConnectConfigLoading } =
    useTQConnectConfig();

  const tqConnectConfig =
    tqConnectConfigResult?.tqConnectConfig as ITQConnectConfig;

  const {
    data: tqProfileCompletionQueries = [],
    isLoading: isTQProfileCompletionQueriesLoading,
  } = useTQProfileCompletionQueries();

  const { data: tqProfileInfo = {}, isLoading: isTQProfileInfoLoading } =
    useTQConnectUserProfileInfo({
      userId,
      userType: 'fan_completed',
    });

  const { submitTQProfileCompletionQuery, isPending: isSaving } =
    useSubmitTQProfileCompletionQuery(userId);

  const { tqConnectScore, tqConnectProfileCompletionResponses } =
    tqProfileInfo as ITQProfileInfo;

  const tqProfileCompletionQueriesWithResponse = useMemo<
    ITQProfileCompletionQueryWithResponse[]
  >(() => {
    return tqProfileCompletionQueries?.map(
      (question: ITQProfileCompletionQuery) => {
        const answer = tqConnectProfileCompletionResponses?.find(
          (q) => q.questionId === question._id,
        );
        return {
          ...question,
          questionId: question._id,
          question: question.question,
          order: question.order,
          selectedOptions: answer?.selectedOptions,
          subjectiveAnswer: answer?.subjectiveAnswer,
        };
      },
    );
  }, [tqConnectProfileCompletionResponses, tqProfileCompletionQueries]);

  const [profileCompletionState, setProfileCompletionState] =
    useState<ITQProfileCompletionState>({
      assignedTQScore: tqConnectScore,
      tqConnectProfileCompletionResponses:
        tqProfileCompletionQueriesWithResponse,
      isTQScoreEvaluated: false,
    });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);

  const { hasPrevQuestion, hasNextQuestion } = useMemo(() => {
    return {
      hasPrevQuestion: currentIndex > 0,
      hasNextQuestion: currentIndex < tqProfileCompletionQueries?.length - 1,
    };
  }, [currentIndex, tqProfileCompletionQueries?.length]);

  const questionsMap = useMemo<{
    [key: string]: ITQProfileCompletionQueryWithResponse;
  }>(() => {
    return profileCompletionState.tqConnectProfileCompletionResponses?.reduce(
      (acc, question) => {
        return {
          ...acc,
          [question._id]: {
            ...question,
          },
        };
      },
      {} as { [key: string]: ITQProfileCompletionQueryWithResponse },
    );
  }, [profileCompletionState.tqConnectProfileCompletionResponses]);

  const questionsMapByIndex = useMemo<{
    [key: number]: ITQProfileCompletionQueryWithResponse;
  }>(() => {
    return profileCompletionState.tqConnectProfileCompletionResponses.reduce(
      (acc, question, index) => ({
        ...acc,
        [index]: {
          ...question,
        },
      }),
      {} as { [key: number]: ITQProfileCompletionQueryWithResponse },
    );
  }, [profileCompletionState.tqConnectProfileCompletionResponses]);

  const updateProfileCompletionState = useCallback(
    (newCompletionState: Partial<ITQProfileCompletionState>) => {
      setProfileCompletionState((prev) =>
        prev ? { ...prev, ...newCompletionState } : prev,
      );
    },
    [],
  );

  const updateQuestionsMap = useCallback(
    (
      questionId: string,
      question: Partial<ITQProfileCompletionQueryWithResponse>,
    ) => {
      setProfileCompletionState((prev) => {
        if (!prev) return prev;

        const list = prev.tqConnectProfileCompletionResponses.map((q) =>
          q.questionId === questionId ? { ...q, ...question } : q,
        );

        return {
          ...prev,
          tqConnectProfileCompletionResponses: list,
        };
      });
    },
    [],
  );

  const openCompletionScreen = useCallback(() => {
    setShowCompletionScreen(true);
  }, []);

  const goToNextUnattemptedQuestion = useCallback(() => {
    const total = Object.keys(questionsMapByIndex).length;

    for (let offset = 1; offset < total; offset += 1) {
      // const i = (currentIndex + offset) % total;
      const i = currentIndex + offset;

      // Stop if we've reached the end of the array
      if (i >= total) {
        break;
      }

      const attempt = { ...questionsMapByIndex[i] };

      const isUnattempted =
        !attempt ||
        (!attempt.selectedOptions?.length && !attempt.subjectiveAnswer?.trim());

      if (isUnattempted) {
        setCurrentIndex(i);
        return;
      }
    }

    // If no unattempted question found in any direction
    openCompletionScreen();
  }, [currentIndex, openCompletionScreen, questionsMapByIndex]);

  const goToPrevOrNextQuestion = useCallback(
    (type: 'prev' | 'next') => {
      setCurrentIndex((prev) => {
        const next = type === 'next' ? prev + 1 : prev - 1;
        return Math.min(
          Math.max(next, 0),
          tqProfileCompletionQueries.length - 1,
        );
      });
    },
    [tqProfileCompletionQueries.length],
  );

  const saveQuestionAnswer = useCallback(
    async (
      questionId: string,
      action: 'save' | 'submit' | 'clear',
      data: ITQProfileCompletionResponseRequest,
      saveToServer = false,
    ) => {
      if (!questionId) return;

      const prevState = {
        ...(profileCompletionState || {}),
      } as ITQProfileCompletionState;

      // if (action === 'submit') {
      //   setIsSaving(true);
      // }

      updateProfileCompletionState({
        tqConnectProfileCompletionResponses:
          prevState.tqConnectProfileCompletionResponses?.map((q) =>
            q._id === questionId
              ? {
                  ...q,
                  ...data,
                }
              : q,
          ),
      });

      // if saveToServer is false (e.g. when typing in subjective answer)
      if (!saveToServer) {
        return;
      }

      submitTQProfileCompletionQuery(data, {
        onSuccess: () => {
          if (action === 'submit') {
            goToNextUnattemptedQuestion();
          }
        },
        onError: () => {
          setProfileCompletionState(prevState); // revert to previous state on error
        },
      });
    },
    [
      profileCompletionState,
      updateProfileCompletionState,
      submitTQProfileCompletionQuery,
      goToNextUnattemptedQuestion,
    ],
  );

  const submitTQProfileCompletionResponse = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async (_blockToastAndClose?: boolean) => {
      if (!userId) return;

      onClose();
      onSubmitSuccess?.();
    },
    [onClose, onSubmitSuccess, userId],
  );

  const reset = useCallback(() => {
    setProfileCompletionState(profileCompletionState);
    setCurrentIndex(0);
    setShowCompletionScreen(false);
  }, [profileCompletionState]);

  const closeSubmitTQProfileModal = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  useEffect(() => {
    setProfileCompletionState({
      assignedTQScore: tqConnectScore,
      tqConnectProfileCompletionResponses:
        tqProfileCompletionQueriesWithResponse,
      isTQScoreEvaluated: Boolean(tqConnectScore),
    });
  }, [tqConnectScore, tqProfileCompletionQueriesWithResponse]);

  const valuesToPass: ContextProps = useMemo(
    () => ({
      userId,
      tqConnectConfig,
      tqProfileCompletionQueries,
      tqProfileCompletionState: profileCompletionState,
      questionsMap,
      questionsMapByIndex,
      currentIndex,
      setCurrentIndex,
      hasPrevQuestion,
      hasNextQuestion,
      isLoading:
        isTQConnectConfigLoading ||
        isTQProfileCompletionQueriesLoading ||
        isTQProfileInfoLoading,
      isSaving,
      showCompletionScreen,
      setShowCompletionScreen,
      closeSubmitTQProfileModal,
      updateTQProfileCompletionState: updateProfileCompletionState,
      updateQuestionsMap,
      saveQuestionAnswer,
      openCompletionScreen,
      submitTQProfileCompletionResponse,
      goToNextUnattemptedQuestion,
      goToPrevOrNextQuestion,
      reset,
    }),
    [
      userId,
      isTQConnectConfigLoading,
      tqConnectConfig,
      tqProfileCompletionQueries,
      profileCompletionState,
      questionsMap,
      questionsMapByIndex,
      currentIndex,
      hasPrevQuestion,
      hasNextQuestion,
      isTQProfileCompletionQueriesLoading,
      isTQProfileInfoLoading,
      isSaving,
      showCompletionScreen,
      closeSubmitTQProfileModal,
      updateProfileCompletionState,
      updateQuestionsMap,
      saveQuestionAnswer,
      openCompletionScreen,
      submitTQProfileCompletionResponse,
      goToNextUnattemptedQuestion,
      goToPrevOrNextQuestion,
      reset,
    ],
  );

  return (
    <SubmitTQProfileContext.Provider value={valuesToPass as ContextProps}>
      {children}
    </SubmitTQProfileContext.Provider>
  );
};
